/**
 * isDemo (SIRENE-imported, unclaimed) profile = quote-request funnel.
 *
 * Replaces the standard profile UI for ~110k SIRENE pros that have not
 * claimed their listing. Goal: convert visitor into a lead matched to
 * CLAIMED competitors in the same (category, department).
 *
 * Anti-thin-content: per-page uniqueness comes from
 *   - company name + SIRET + address (different per pro)
 *   - city + departmentCode (different per pro)
 *   - similarPros list (different per dept+category)
 *   - weeklyLeadCount (different per category+department)
 *   - category-specific FAQ
 */
import { Link } from 'react-router'
import { ArrowRight, MapPin, Building2, ShieldCheck, Sparkles, Phone, CheckCircle, Activity, FileCheck } from 'lucide-react'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { InlineQuiz } from '~/components/inline-quiz'
import { LocalBusinessSchema, BreadcrumbSchema } from '~/components/structured-data'

interface DemoPro {
  id: string
  slug: string
  companyName: string
  siret: string
  city: string
  postalCode: string
  departmentCode: string
  addressLine1: string | null
  addressLine2: string | null
  categories: Array<{ id: string; name: string; slug: string }>
}

interface SimilarPro {
  id: string
  companyName: string
  slug: string
  city: string
  ratingAvg: string | null
  reviewCount: number
  identityVerified: boolean
  isDemo: boolean
}

interface Props {
  pro: DemoPro
  similarPros: SimilarPro[]
  weeklyLeadCount: number
}

// Category FAQ — small but per-slug, ensures content differs across pages.
// Keys correspond to common serviceCategories.slug values. Fallback below
// renders generic FAQ if slug not matched.
const CATEGORY_FAQ: Record<string, Array<{ q: string; a: string }>> = {
  plombier: [
    { q: 'Combien coûte un plombier en 2026 ?', a: 'En moyenne 50-70€/h en France, avec un déplacement de 30-50€. Les urgences (nuit, dimanche) majorent de 50-100%.' },
    { q: 'Comment être sûr d\'un plombier certifié ?', a: 'Demandez la garantie décennale et le numéro SIRET. AfficheServices vérifie automatiquement le SIRET et l\'activité de chaque artisan.' },
    { q: 'Quel délai pour une réparation de plomberie ?', a: 'Pour une fuite urgente, intervention possible sous 2-4h. Pour des travaux planifiés, comptez 5-15 jours selon la disponibilité.' },
  ],
  electricien: [
    { q: 'Quel est le tarif d\'un électricien ?', a: 'De 35€ à 60€/h selon la région et la complexité. Les diagnostics électriques varient de 100€ à 200€.' },
    { q: 'Pourquoi un électricien certifié Qualifelec ?', a: 'Qualifelec atteste de la conformité aux normes NF C 15-100. Indispensable pour les rénovations électriques et nouvelles installations.' },
    { q: 'L\'électricien doit-il être assuré ?', a: 'Oui, la garantie décennale est obligatoire pour tous travaux d\'installation électrique. Vérifiez l\'attestation avant signature.' },
  ],
  chauffagiste: [
    { q: 'Combien coûte l\'entretien annuel d\'une chaudière ?', a: 'Entre 90€ et 180€ TTC selon la chaudière (gaz, fioul, pompe à chaleur). L\'entretien est obligatoire chaque année.' },
    { q: 'Quel chauffagiste pour une pompe à chaleur ?', a: 'Exigez la qualification RGE QualiPAC. Elle conditionne les aides MaPrimeRénov\' et la TVA réduite à 5,5%.' },
    { q: 'Combien de temps pour remplacer une chaudière ?', a: '1 à 2 jours pour un remplacement simple. Comptez plus pour passer d\'une énergie à une autre (gaz → PAC).' },
  ],
  menuisier: [
    { q: 'Tarif d\'un menuisier en 2026 ?', a: 'De 40€ à 70€/h. Pour une fenêtre standard posée, comptez 350€ à 800€. Pour un escalier sur mesure, 2 500€ à 8 000€.' },
    { q: 'Menuisier RGE — quand est-ce utile ?', a: 'Indispensable pour les fenêtres si vous voulez bénéficier de MaPrimeRénov\' ou de la TVA à 5,5% sur les travaux d\'isolation.' },
  ],
  peintre: [
    { q: 'Combien coûte un peintre au m² ?', a: 'De 25€ à 45€/m² incluant fournitures, selon l\'état du support et la finition souhaitée. Hors préparation lourde.' },
    { q: 'Quelle TVA pour les travaux de peinture ?', a: 'TVA à 10% pour un logement de plus de 2 ans. TVA à 5,5% si peinture intégrée à un projet de rénovation énergétique.' },
  ],
  maçon: [
    { q: 'Tarif d\'un maçon en 2026 ?', a: 'De 45€ à 75€/h. Pour une ouverture de mur porteur, comptez 1 500€ à 4 000€ étude technique comprise.' },
    { q: 'Faut-il un permis pour des travaux de maçonnerie ?', a: 'Oui pour toute modification de façade, surface créée >20 m² ou ouverture sur mur porteur. Le maçon vous oriente vers le bon dossier.' },
  ],
}

const FALLBACK_FAQ: Array<{ q: string; a: string }> = [
  { q: 'Comment fonctionne AfficheServices ?', a: 'Décrivez votre projet en 30 secondes via notre formulaire — nous le transmettons à 3 artisans vérifiés dans votre département. Vous recevez les devis sous 48h.' },
  { q: 'Est-ce vraiment gratuit ?', a: 'Oui, 100% gratuit pour les particuliers. Aucune carte bancaire requise, aucun engagement.' },
  { q: 'Comment êtes-vous sûrs des artisans ?', a: 'Nous vérifions SIRET, activité INSEE, assurance décennale et garantie décennale. Chaque artisan a une note moyenne basée sur des avis vérifiés.' },
]

export function ProDemoFunnel({ pro, similarPros, weeklyLeadCount }: Props) {
  const cat = pro.categories[0]
  const catName = cat?.name ?? 'artisan'
  const catSlug = cat?.slug ?? ''
  const quoteHref = `/demande-devis/1${cat?.id ? `?cat=${cat.id}` : ''}`
  const claimedPros = similarPros.filter(p => !p.isDemo).slice(0, 3)
  const faq = CATEGORY_FAQ[catSlug] ?? FALLBACK_FAQ

  const profileUrl = `https://www.afficheservices.fr/pro/${pro.slug}`

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      {/* Sites de lieux carousel signal — SIRENE-imported businesses still
          carry a real address. LocalBusinessSchema with isPartOf → aggregator
          Organization is what eligible Google "Sites de lieux" entries need.
          AggregateRating skipped (no reviews on demo). */}
      <LocalBusinessSchema pro={{
        companyName: pro.companyName,
        description: null,
        city: pro.city,
        postalCode: pro.postalCode,
        addressLine1: pro.addressLine1,
        ratingAvg: null,
        reviewCount: 0,
        slug: pro.slug,
        latitude: null,
        longitude: null,
        logoUrl: null,
        coverImageUrl: null,
        primaryCategorySlug: cat?.slug ?? null,
        primaryCategoryName: cat?.name ?? null,
        serviceAreaDepartments: [pro.departmentCode],
      }} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Professionnels', url: 'https://www.afficheservices.fr/services' },
        { name: pro.companyName, url: profileUrl },
      ]} />

      {/* Fiche annuaire banner — always visible to disclose status */}
      <div className="border-b border-blue-200 bg-blue-50">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-2.5 lg:px-6">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-600" />
            <p className="text-xs text-blue-800">
              <span className="font-semibold">Fiche annuaire</span> — Données SIRENE (INSEE). Ce professionnel n'a pas encore revendiqué son profil.
            </p>
          </div>
          <Link to={`/revendiquer?siret=${pro.siret}`} className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-hover">
            Vous êtes ce pro ?
          </Link>
        </div>
      </div>

      {/* ── Hero: company name + quote CTA ──────────────── */}
      <section className="bg-gradient-to-br from-primary via-blue-600 to-indigo-700 text-white">
        <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6 lg:py-16">
          <p className="text-sm font-medium uppercase tracking-wider text-blue-100">
            {catName} · {pro.city} ({pro.departmentCode})
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">
            {pro.companyName}
          </h1>
          <p className="mt-4 text-base text-blue-50 sm:text-lg">
            Demandez gratuitement <strong>3 devis comparatifs</strong> de {catName.toLowerCase()}
            {' '}à {pro.city} — réponse des artisans vérifiés sous 48h.
          </p>

          {weeklyLeadCount > 0 && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur">
              <Activity className="h-4 w-4 text-amber-200" />
              <span className="text-sm">
                <strong>{weeklyLeadCount}</strong> demande{weeklyLeadCount > 1 ? 's' : ''} de {catName.toLowerCase()} dans le {pro.departmentCode} cette semaine
              </span>
            </div>
          )}

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to={quoteHref}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-4 text-base font-bold text-primary shadow-lg hover:bg-blue-50 btn-press"
            >
              Demander 3 devis gratuits <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to={`/revendiquer?siret=${pro.siret}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 px-6 py-4 text-base font-medium text-white hover:bg-white/10"
            >
              Je suis {pro.companyName}
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 text-sm text-blue-50 sm:grid-cols-3">
            <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-300" /> 100% gratuit, sans engagement</div>
            <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-300" /> Réponse sous 48h</div>
            <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-emerald-300" /> Artisans vérifiés et assurés</div>
          </div>
        </div>
      </section>

      {/* ── Inline quiz — primary conversion surface ───────── */}
      <section className="-mt-6 bg-surface-secondary">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <div className="rounded-2xl border border-border bg-white p-1 shadow-xl">
            <InlineQuiz
              serviceName={catName.toLowerCase()}
              cityName={pro.city}
              categorySlug={catSlug || 'plombier'}
            />
          </div>
        </div>
      </section>

      {/* ── SIRENE info compact ──────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-8 lg:px-6">
          <h2 className="text-base font-bold text-slate-900">Informations légales — Source SIRENE / INSEE</h2>
          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <dt className="text-[11px] uppercase text-slate-500">SIRET</dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">{pro.siret}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase text-slate-500">Statut</dt>
              <dd className="mt-1 flex items-center gap-1 text-sm font-semibold text-emerald-700">
                <ShieldCheck className="h-4 w-4" /> Entreprise active
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase text-slate-500">Activité</dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">{catName}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase text-slate-500">Localisation</dt>
              <dd className="mt-1 flex items-start gap-1 text-sm font-semibold text-slate-900">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
                <span>{pro.postalCode} {pro.city}</span>
              </dd>
            </div>
          </dl>
          <p className="mt-5 rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
            <strong>Note :</strong> AfficheServices ne communique pas les coordonnées téléphoniques
            des entreprises figurant sur les fiches annuaire. Pour être mis en relation,
            utilisez le formulaire de devis ci-dessus — nous transmettons votre demande
            aux artisans disponibles dans le {pro.departmentCode}.
          </p>
        </div>
      </section>

      {/* ── Claimed competitors ──────────────── */}
      {claimedPros.length > 0 && (
        <section className="bg-surface-secondary">
          <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-bold text-slate-900">
                {catName}s vérifiés près de {pro.city}
              </h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Profils complets, avis vérifiés, contact direct activé.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {claimedPros.map(p => (
                <Link
                  key={p.id}
                  to={`/pro/${p.slug}`}
                  className="group rounded-xl border border-border bg-white p-4 transition-all hover:border-primary hover:shadow-md"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-base font-bold text-primary">
                      {p.companyName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900 group-hover:text-primary">{p.companyName}</p>
                      <p className="text-xs text-slate-500">{p.city}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs">
                    {p.identityVerified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-primary">
                        <ShieldCheck className="h-3 w-3" /> Vérifié
                      </span>
                    )}
                    {Number(p.ratingAvg) > 0 && (
                      <span className="text-slate-600">★ {p.ratingAvg} ({p.reviewCount})</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Mini-FAQ ──────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes — {catName} {pro.city}</h2>
          <div className="mt-5 space-y-3">
            {faq.map((item) => (
              <details key={item.q} className="group rounded-xl border border-border bg-white p-4">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                  <span>{item.q}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ──────────────── */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="mx-auto max-w-3xl px-4 py-12 text-center lg:px-6">
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">
            Prêt à recevoir vos 3 devis {catName.toLowerCase()} à {pro.city} ?
          </h2>
          <p className="mt-3 text-sm text-slate-600">
            Réponse des artisans sous 48h. Comparez les prix, choisissez en confiance.
          </p>
          <Link
            to={quoteHref}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-4 text-base font-bold text-white shadow-md hover:bg-primary-hover btn-press"
          >
            Lancer ma demande gratuite <ArrowRight className="h-5 w-5" />
          </Link>
          <p className="mt-3 text-xs text-slate-500">
            <FileCheck className="inline h-3.5 w-3.5 align-text-bottom" /> 30 secondes · Sans carte bancaire · Sans engagement
          </p>
        </div>
      </section>

      <Footer />

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-16 z-40 border-t border-border bg-white/95 p-3 backdrop-blur-sm lg:hidden lg:bottom-0">
        <Link
          to={quoteHref}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-primary-hover btn-press"
        >
          <Phone className="h-4 w-4" /> Recevoir 3 devis gratuits <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="h-20 lg:hidden" />
    </div>
  )
}
